import { analyzeRuleGraph } from "./analysis";
import { useEffect, useMemo } from "react";

import type { RuleGraphDto } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";
import { useAgenticLogicScenarioEvaluation } from "@src/api/agentic-logic/evaluate-agentic-logic-rule-scenario";
import { useProductAndVariantSummaries } from "@src/graphql/hooks/useProductAndVariantSummaries";
import { useDispatch, useSelector } from "@src/store";
import { getAtlasActiveScenario, setAiChat } from "@src/store/slices/global";

import { buildCartInput, cartLineLookupId } from "./cart-input";
import { cartInputTargetId } from "./layout";
import { buildRunWalkPlan, sliceRunWalkOverlays } from "./run-walk";
import { buildScenarioContext } from "./scenario-context";
import { buildScenarioOverlays } from "./scenario-overlays";
import { useRuleGraphRunWalk } from "./useRuleGraphRunWalk";
import {
  edgeOverlayId,
  RULE_GRAPH_CART_INPUT_NODE_ID,
  type RuleGraphOverlaysByEdgeId,
  type RuleGraphOverlaysByNodeId,
} from "./types";

/**
 * Joins the cart run Atlas last produced onto the graph.
 *
 * Running a cart is a conversation now: the chat calls the evaluate tool and
 * seeds its result into the evaluation cache, so this hook only READS — it
 * never asks for an evaluation of its own. That is what keeps one path to a
 * run, and it is why the run identity (`ruleId`, scenario `name`, `runToken`)
 * arrives through the Atlas chat state rather than from a control on the
 * canvas.
 *
 * A run outlives only the draft it ran against: saving a draft drops the
 * cached evaluations, and this hook lets the run go with them rather than
 * animating a route the rule no longer takes.
 */
export const useScenarioPreview = ({
  ruleId,
  graph,
}: {
  ruleId: string;
  graph: RuleGraphDto | null;
}): {
  overlaysByNodeId: RuleGraphOverlaysByNodeId | undefined;
  overlaysByEdgeId: RuleGraphOverlaysByEdgeId | undefined;
} => {
  const dispatch = useDispatch();
  const activeScenario = useSelector(getAtlasActiveScenario);
  const run = activeScenario?.ruleId === ruleId ? activeScenario : null;
  const evaluationQuery = useAgenticLogicScenarioEvaluation(
    ruleId,
    run?.name ?? null
  );

  // The cache entry is the run. A draft save removes the family, so an
  // evaluation that has gone means the rule has changed underneath it —
  // release the run rather than leave a stale route lit.
  const hasEvaluation = evaluationQuery.data !== undefined;
  useEffect(() => {
    if (run === null || hasEvaluation) {
      return;
    }
    dispatch(setAiChat({ key: "activeScenario", value: null }));
  }, [run, hasEvaluation]);

  // The surface and delivery the run was judged on, from the request the
  // chat handed the evaluate tool — the one source for a named example cart
  // and a chat-authored one alike.
  const runRequest = run?.request;
  const scenarioContext = useMemo(
    () => (runRequest ? buildScenarioContext(runRequest) : undefined),
    [runRequest]
  );

  // "What the shopper sees" shows the real product photos, and the cart
  // panel names what the shopper had. The evaluation response and the
  // request both carry only ids, so titles and pictures come from the
  // admin's own Shopify GraphQL session — ONE lookup per evaluation covering
  // both, keyed on the most specific id each item has.
  const hydratedItems = evaluationQuery.data?.hydratedItems ?? [];
  const cartLineIds = (runRequest?.cart?.lines ?? []).map(cartLineLookupId);
  const { imageUrlByGid, summaryByGid } = useProductAndVariantSummaries([
    ...new Set([
      ...hydratedItems.map((item) => item.variantId ?? item.id),
      ...cartLineIds,
    ]),
  ]);

  const cartInput =
    graph !== null && runRequest !== undefined
      ? buildCartInput({
          request: runRequest,
          graph,
          summaryByGid,
          imageUrlByGid,
        })
      : null;

  const scenarioOverlays =
    graph !== null && run !== null && evaluationQuery.data
      ? buildScenarioOverlays({
          graph,
          coverage: evaluationQuery.data.coverage,
          telemetry: evaluationQuery.data.result.telemetry,
          exclusions: evaluationQuery.data.exclusions,
          hydratedItems: evaluationQuery.data.hydratedItems,
          // Absent only on tool outputs recorded before the field existed —
          // "hide" restates what those runs displayed then.
          onEmpty: evaluationQuery.data.onEmpty ?? "hide",
          imageUrlByGid,
          scenarioContext,
        })
      : null;

  // The cart at the head of the rows rides on the renderer's own node, with
  // its edge into the first row header — only where the layout has honest
  // rows to head, which is also the only place the run walk exists.
  const cartTargetId =
    graph !== null && cartInput !== null
      ? cartInputTargetId(
          graph,
          analyzeRuleGraph(graph),
          evaluationQuery.data?.coverage
        )
      : undefined;
  const hasCartInput =
    scenarioOverlays !== null &&
    cartInput !== null &&
    cartTargetId !== undefined;
  const overlays =
    scenarioOverlays !== null && hasCartInput
      ? {
          byNodeId: {
            ...scenarioOverlays.byNodeId,
            [RULE_GRAPH_CART_INPUT_NODE_ID]: { cartInput },
          },
          byEdgeId: {
            ...scenarioOverlays.byEdgeId,
            [edgeOverlayId({
              from: RULE_GRAPH_CART_INPUT_NODE_ID,
              to: cartTargetId,
              kind: "flow",
            })]: {},
          },
        }
      : scenarioOverlays;

  // The run walk: the same final overlays, time-sliced along the route the
  // evaluation took. A null plan (no coverage, revision mismatch, or a
  // graph only the base layout could place) degrades to the static
  // overlays — no walk, no regression.
  const runKey = run !== null ? `${run.name}:${run.runToken}` : null;
  const plan =
    graph !== null && overlays !== null && evaluationQuery.data
      ? buildRunWalkPlan({
          graph,
          coverage: evaluationQuery.data.coverage,
          hasCartInput,
        })
      : null;
  const frame = useRuleGraphRunWalk({ plan, runKey });
  const walkedOverlays =
    graph !== null && overlays !== null && plan !== null && frame !== null
      ? sliceRunWalkOverlays({
          graph,
          plan,
          frame,
          finalByNodeId: overlays.byNodeId,
          finalByEdgeId: overlays.byEdgeId,
        })
      : overlays;

  return {
    overlaysByNodeId: walkedOverlays?.byNodeId,
    overlaysByEdgeId: walkedOverlays?.byEdgeId,
  };
};
